import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ShieldCheck, FileText, AlertTriangle, Calendar, Building2,
  Award, Users, TrendingDown,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StatusBadge } from "@/components/StatusBadge";
import { StarRating } from "@/components/StarRating";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { api, formatDateBR } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";

const KpiCard = ({ icon: Icon, label, value, hint, accent, to, testId }) => {
  const navigate = useNavigate();
  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={() => to && navigate(to)}
      onKeyDown={(e) => { if (to && (e.key === "Enter" || e.key === " ")) navigate(to); }}
      className="card-soft cursor-pointer transition-all hover:shadow-md hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-midnight"
      data-testid={testId}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
            <div className="mt-2 text-2xl font-semibold text-slate-900">{value}</div>
            {hint && <div className="mt-1 text-xs text-slate-500">{hint}</div>}
          </div>
          <div className={`p-2 rounded-md ${accent}`}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

function expirationBadge(days) {
  if (days < 0) return { label: "Vencido", cls: "bg-red-100 text-red-800 border-red-300" };
  if (days <= 15) return { label: `${days} dias`, cls: "bg-red-50 text-red-800 border-red-200" };
  if (days <= 30) return { label: `${days} dias`, cls: "bg-amber-50 text-amber-800 border-amber-200" };
  return { label: `${days} dias`, cls: "bg-emerald-50 text-emerald-800 border-emerald-200" };
}

export default function DiretoraDashboard() {
  const [data, setData] = useState(null);
  const [ranking, setRanking] = useState([]);

  useEffect(() => {
    api.get("/dashboard/").then(({ data }) => setData(data));
    api.get("/ratings/ranking", { params: { period: "month" } })
      .then(({ data }) => setRanking(data.ranking || []))
      .catch(() => {});
  }, []);

  if (!data) return <Skeleton className="h-96" />;

  const { kpis, extras, contracts } = data;
  const dueSoon = extras?.contracts_due_60 || [];
  const overdue = extras?.contracts_overdue || [];

  return (
    <div className="space-y-6" data-testid="diretora-dashboard">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Painel Executivo</h1>
        <p className="text-sm text-slate-600 mt-1">
          Visão consolidada da carteira de contratos, riscos e aprovações. Clique nos cards para navegar.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <KpiCard
          icon={FileText}
          label="Contratos ativos"
          value={kpis.active_contracts}
          accent="bg-emerald-50 text-emerald-700"
          hint={`${kpis.total_contracts} no total`}
          to="/diretora/contratos?status=active"
          testId="kpi-diretora-ativos"
        />
        <KpiCard
          icon={ShieldCheck}
          label="Aprovações pendentes"
          value={(extras?.pending_consultant_approvals || 0) + (extras?.pending_director_docs || 0) + (extras?.pending_director_reports || 0)}
          accent="bg-amber-50 text-amber-700"
          hint="Designações + docs + prestações"
          to="/diretora/aprovacoes"
          testId="kpi-diretora-aprovacoes"
        />
        <KpiCard
          icon={AlertTriangle}
          label="Em risco"
          value={kpis.in_risk}
          accent="bg-red-50 text-red-700"
          hint="Menos de 30 dias"
          to="/diretora/contratos?status=at_risk"
          testId="kpi-diretora-risco"
        />
        <KpiCard
          icon={Calendar}
          label="Em atenção"
          value={kpis.in_attention}
          accent="bg-amber-50 text-amber-700"
          hint="30 a 60 dias"
          to="/diretora/contratos?status=attention"
          testId="kpi-diretora-atencao"
        />
        <KpiCard
          icon={AlertTriangle}
          label="Em atraso"
          value={kpis.overdue || 0}
          accent="bg-rose-100 text-rose-800"
          hint="Prazo vencido"
          to="/diretora/contratos?status=overdue"
          testId="kpi-diretora-atraso"
        />
      </div>

      {overdue.length > 0 && (
        <Card className="card-soft border-rose-200" data-testid="overdue-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2 text-rose-800">
              <AlertTriangle className="w-4 h-4" /> Contratos em atraso ({overdue.length})
              <Link
                to="/diretora/contratos?status=overdue"
                className="ml-auto text-xs font-medium text-midnight hover:underline"
                data-testid="overdue-see-all"
              >
                Ver todos e dar baixa
              </Link>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="space-y-2">
              {overdue.slice(0, 5).map((c, i) => (
                <li key={c.id}>
                  <Link
                    to={`/diretora/contratos/${c.id}`}
                    className="flex items-center justify-between gap-3 p-3 border border-rose-200 rounded-md hover:bg-rose-50/50 transition-colors"
                    data-testid={`overdue-item-${c.id}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="w-7 h-7 rounded-full bg-rose-100 text-rose-800 flex items-center justify-center text-xs font-semibold flex-shrink-0">
                        {i + 1}
                      </span>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-900 truncate flex items-center gap-2 flex-wrap">
                          <Building2 className="w-3.5 h-3.5 text-slate-400" />
                          {c.client_name}
                          <span className="text-slate-400">·</span>
                          <span className="text-slate-600">{c.product_name}</span>
                        </div>
                        <div className="text-xs text-slate-500">
                          Venceu em {formatDateBR(c.end_date)}
                          {c.consultant_name && ` · Consultor: ${c.consultant_name}`}
                        </div>
                      </div>
                    </div>
                    <Badge variant="outline" className="bg-rose-50 text-rose-800 border-rose-200 text-xs flex-shrink-0">
                      {c.days_overdue} dia{c.days_overdue === 1 ? "" : "s"}
                    </Badge>
                  </Link>
                </li>
              ))}
            </ol>
          </CardContent>
        </Card>
      )}

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="card-soft lg:col-span-2" data-testid="due-60-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingDown className="w-4 h-4 text-amber-600" /> Contratos próximos do vencimento (60 dias)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {dueSoon.length === 0 ? (
              <div className="text-sm text-slate-500 py-6 text-center">
                Sem contratos vencendo nos próximos 60 dias.
              </div>
            ) : (
              <ol className="space-y-2">
                {dueSoon.map((c, i) => {
                  const exp = expirationBadge(c.days_remaining);
                  return (
                    <li key={c.id}>
                      <Link to={`/diretora/contratos/${c.id}`}
                        className="flex items-center justify-between gap-3 p-3 border border-slate-200 rounded-md hover:bg-slate-50 hover:border-midnight/30 transition-colors"
                        data-testid={`due-60-item-${c.id}`}>
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="w-7 h-7 rounded-full bg-amber-100 text-amber-800 flex items-center justify-center text-xs font-semibold flex-shrink-0">
                            {i + 1}
                          </span>
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-slate-900 truncate flex items-center gap-2 flex-wrap">
                              <Building2 className="w-3.5 h-3.5 text-slate-400" />
                              {c.client_name}
                              <span className="text-slate-400">·</span>
                              <span className="text-slate-600">{c.product_name}</span>
                            </div>
                            <div className="text-xs text-slate-500">
                              Vence em {formatDateBR(c.end_date)}{c.consultant_name && ` · Consultor: ${c.consultant_name}`}
                            </div>
                          </div>
                        </div>
                        <Badge variant="outline" className={`${exp.cls} border text-xs flex-shrink-0`}>
                          {exp.label}
                        </Badge>
                      </Link>
                    </li>
                  );
                })}
              </ol>
            )}
          </CardContent>
        </Card>

        <Card className="card-soft">
          <CardHeader>
            <CardTitle className="text-base">Aprovações</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Link to="/diretora/aprovacoes" className="flex items-center justify-between p-3 border border-slate-200 rounded-md hover:bg-slate-50 hover:border-midnight/30 transition-colors" data-testid="aprovacao-designacoes">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-amber-700" />
                <span className="text-sm">Designações</span>
              </div>
              <span className="text-sm font-semibold text-slate-900">{extras?.pending_consultant_approvals || 0}</span>
            </Link>
            <Link to="/diretora/aprovacoes" className="flex items-center justify-between p-3 border border-slate-200 rounded-md hover:bg-slate-50 hover:border-midnight/30 transition-colors" data-testid="aprovacao-documentos">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-blue-700" />
                <span className="text-sm">Documentos</span>
              </div>
              <span className="text-sm font-semibold text-slate-900">{extras?.pending_director_docs || 0}</span>
            </Link>
            <Link to="/diretora/aprovacoes" className="flex items-center justify-between p-3 border border-slate-200 rounded-md hover:bg-slate-50 hover:border-midnight/30 transition-colors" data-testid="aprovacao-prestacoes">
              <div className="flex items-center gap-2">
                <Award className="w-4 h-4 text-emerald-700" />
                <span className="text-sm">Prestações</span>
              </div>
              <span className="text-sm font-semibold text-slate-900">{extras?.pending_director_reports || 0}</span>
            </Link>
          </CardContent>
        </Card>
      </div>

      <Card className="card-soft">
        <CardHeader className="flex flex-row justify-between items-center">
          <CardTitle className="text-base flex items-center gap-2">
            <Award className="w-4 h-4 text-gold-deep" /> Top 5 consultores do mês
          </CardTitle>
          <Link to="/diretora/ranking" className="text-xs font-medium text-midnight hover:underline">
            Ver ranking completo
          </Link>
        </CardHeader>
        <CardContent>
          {ranking.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">Sem avaliações neste mês ainda.</div>
          ) : (
            <ol className="grid sm:grid-cols-2 lg:grid-cols-5 gap-2">
              {ranking.slice(0, 5).map((r, i) => (
                <li key={r.consultant_id} className="p-3 border border-slate-200 rounded-md">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-6 h-6 rounded-full bg-midnight text-white flex items-center justify-center text-xs font-semibold">
                      {i + 1}
                    </span>
                    <span className="text-sm font-medium truncate">{r.consultant_name}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <StarRating value={Math.round(r.avg)} readOnly size={14} testIdPrefix={`ddash-${i}`} />
                    <span className="text-sm font-semibold text-gold-deep">{r.avg.toFixed(2)}</span>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </CardContent>
      </Card>

      <Card className="card-soft">
        <CardHeader>
          <CardTitle className="text-base">Carteira de contratos</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {contracts.map((c) => (
              <Link
                key={c.id}
                to={`/diretora/contratos/${c.id}`}
                className="block p-4 border border-slate-200 rounded-lg hover:bg-slate-50"
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Building2 className="w-4 h-4 text-slate-500" />
                      <span className="font-medium text-slate-900">{c.client_name}</span>
                      <span className="text-slate-400">·</span>
                      <span className="text-sm text-slate-600">{c.product_name}</span>
                    </div>
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      <StatusBadge status={c.status} />
                      <StatusBadge status={c.risk} />
                      {c.consultant_name && (
                        <span className="text-xs text-slate-500">Consultor: {c.consultant_name}</span>
                      )}
                    </div>
                  </div>
                  <div className="text-right text-xs text-slate-500">
                    <div>Vence: {formatDateBR(c.end_date)}</div>
                    <div className={c.days_remaining < 30 ? "text-red-600 font-medium" : ""}>
                      {c.days_remaining < 0 ? "Vencido" : `${c.days_remaining} dias`}
                    </div>
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-3">
                  <Progress value={c.progress_pct} className="h-1.5 flex-1" />
                  <span className="text-xs text-slate-600 w-12 text-right">{c.progress_pct}%</span>
                </div>
              </Link>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
